import { TrackedObject } from 'tracked-built-ins';
import { gt } from 'ember-truth-helpers';
import { htmlSafe } from '@ember/template';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { testAwareTimeout, waitForNext } from 'ember-simplepractice/utils/waiters';
import Component from '@glimmer/component';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import styles from './messaging-carousel.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const PANEL_TRANSITION_MS = 200;

export default class MessagingCarousel extends Component {
  @service intl;

  #state = new TrackedObject({
    selectedKey: this.#keyFor(this.args.items?.[0]),
    isExiting: false,
  });

  // Stores last active item so it can be render during the exit animation
  #lastActiveItem;

  get incomingItems() {
    return this.args.items ?? [];
  }

  get listKey() {
    return this.args.key;
  }

  get selectedKey() {
    return this.#state.selectedKey;
  }

  get exitingItem() {
    const incoming = this.incomingItems;
    const snapshot = this.#lastActiveItem;

    if (incoming.length === 0 || snapshot === undefined) {
      return undefined;
    }

    // Detect whether the active item still exists in the incoming items
    const stillPresent = incoming.some(item => this.#keyFor(item) === this.selectedKey);

    // If the active item still exists in the incoming items, or the exit animation has finished
    // return undefined and stop rendering the snapshot item
    if (stillPresent || this.#keyFor(snapshot) !== this.selectedKey) {
      return undefined;
    }

    return snapshot;
  }

  // Temporary render the outgoing item alongside the new list to allow for the transition animation
  get items() {
    const exitingItem = this.exitingItem;
    return exitingItem === undefined ? this.incomingItems : [exitingItem, ...this.incomingItems];
  }

  get length() {
    return this.items.length;
  }

  get activeIndex() {
    if (this.exitingItem !== undefined) {
      return this.#state.isExiting ? 1 : 0;
    }

    const index = this.items.findIndex(item => this.#keyFor(item) === this.#state.selectedKey);
    return Math.max(0, index);
  }

  get ariaLabel() {
    return this.args.ariaLabel ?? this.intl.t('emc.carousel.default_aria_label');
  }

  get ariaLive() {
    return this.args.ariaLive ?? 'off';
  }

  get hasControls() {
    return this.incomingItems.length > 1;
  }

  get counterText() {
    const position = this.exitingItem === undefined ? this.activeIndex + 1 : 1;
    return `${position}/${this.incomingItems.length}`;
  }

  get isTransitioning() {
    return this.transitionToIndex.isRunning || this.exitingItem !== undefined;
  }

  get isPreviousDisabled() {
    return this.activeIndex <= 0 || this.isTransitioning;
  }

  get isNextDisabled() {
    return this.activeIndex >= this.length - 1 || this.isTransitioning;
  }

  get isAnimating() {
    return this.transitionToIndex.isRunning || this.#state.isExiting;
  }

  get trackStyle() {
    const duration = this.isAnimating ? PANEL_TRANSITION_MS : 0;

    return htmlSafe(
      `--active-index: ${this.activeIndex}; --panel-transition-duration: ${duration}ms;`
    );
  }

  // Detect whether the active item was removed from the incoming items and trigger the exit animation
  handleExitingItem = modifier((_element, [incoming, selectedKey]) => {
    const active = incoming.find(item => this.#keyFor(item) === selectedKey);

    if (active !== undefined) {
      this.#lastActiveItem = active;
    } else if (
      this.#lastActiveItem !== undefined &&
      this.#keyFor(this.#lastActiveItem) === selectedKey
    ) {
      this.slideOutExitingItem.perform();
    }
  });

  slideOutExitingItem = task({ drop: true }, async () => {
    // Necessary to prevent single computation errors since we are potentially modifying
    // the same property we are reading.
    await waitForNext();

    this.#state.isExiting = true;

    try {
      await testAwareTimeout(PANEL_TRANSITION_MS);

      if (this.exitingItem !== undefined) {
        this.#state.selectedKey = this.#keyFor(this.incomingItems[0]);
      }
    } finally {
      this.#state.isExiting = false;
    }
  });

  #keyFor(item) {
    return this.listKey ? item?.[this.listKey] : item;
  }

  panelAriaHidden = index => {
    return index === this.activeIndex ? undefined : 'true';
  };

  panelInert = index => {
    return index !== this.activeIndex;
  };

  goToPrevious = () => {
    if (this.isPreviousDisabled) {
      return;
    }
    this.transitionToIndex.perform(this.activeIndex - 1);
  };

  goToNext = () => {
    if (this.isNextDisabled) {
      return;
    }
    this.transitionToIndex.perform(this.activeIndex + 1);
  };

  #clampIndex(index) {
    if (this.length === 0) {
      return 0;
    }
    return Math.min(Math.max(0, index), this.length - 1);
  }

  transitionToIndex = task({ drop: true }, async index => {
    const clamped = this.#clampIndex(index);
    if (clamped === this.activeIndex) {
      return;
    }

    this.#state.selectedKey = this.#keyFor(this.items[clamped]);

    await testAwareTimeout(PANEL_TRANSITION_MS);
  });

  <template>
    {{#if (gt this.length 0)}}
      <div
        class="{{styles.component}} messaging-carousel"
        aria-label={{this.ariaLabel}}
        role="region"
        data-test-messaging-carousel
        {{this.handleExitingItem this.incomingItems this.selectedKey}}
        ...attributes
      >
        <div class="viewport" aria-live={{this.ariaLive}}>
          <div class="track" style={{this.trackStyle}}>
            {{#each this.items key=this.listKey as |item index|}}
              <div
                class="panel"
                aria-hidden={{this.panelAriaHidden index}}
                inert={{this.panelInert index}}
                data-test-panel
              >
                {{yield item}}
              </div>
            {{/each}}
          </div>
        </div>

        {{#if this.hasControls}}
          <div class="controls" data-test-controls>
            <GhostButton
              @disabled={{this.isPreviousDisabled}}
              @fitContent={{true}}
              @iconOnly={{true}}
              aria-label={{t "emc.carousel.previous"}}
              {{on "click" this.goToPrevious}}
              class="back-button"
              data-test-previous
            >
              {{svgJar "messaging-chevron-left"}}
            </GhostButton>

            <span class="counter" data-test-counter>
              {{this.counterText}}
            </span>

            <GhostButton
              @disabled={{this.isNextDisabled}}
              @fitContent={{true}}
              @iconOnly={{true}}
              aria-label={{t "emc.carousel.next"}}
              {{on "click" this.goToNext}}
              class="next-button"
              data-test-next
            >
              {{svgJar "messaging-chevron-right"}}
            </GhostButton>
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
